export type ScrollSpySection = {
  id: string;
  top: number;
};

/**
 * Last section whose top has crossed the sticky offset line (viewport coordinates).
 * Uses bounding-rect tops so it stays correct regardless of offsetParent / mobile scroll.
 */
export function resolveWindowScrollActiveSectionId({
  sections,
  offset,
  isAtBottom,
  isAncestor,
}: {
  sections: ScrollSpySection[];
  offset: number;
  isAtBottom: boolean;
  /** When set, a parent category loses to a descendant whose title has also crossed. */
  isAncestor?: (ancestorId: string, descendantId: string) => boolean;
}): string | undefined {
  if (!sections.length) {
    return undefined;
  }

  const sorted = [...sections].sort((a, b) => a.top - b.top);

  if (isAtBottom) {
    return leafSection(sorted, isAncestor).id;
  }

  const crossed = sorted.filter(section => section.top <= offset);
  if (!crossed.length) {
    return undefined;
  }

  return leafSection(crossed, isAncestor).id;
}

function leafSection(
  candidates: ScrollSpySection[],
  isAncestor?: (ancestorId: string, descendantId: string) => boolean,
): ScrollSpySection {
  const leaves = isAncestor
    ? candidates.filter(
        section =>
          !candidates.some(
            other => other.id !== section.id && isAncestor(section.id, other.id),
          ),
      )
    : candidates;
  const pool = leaves.length ? leaves : candidates;
  return pool[pool.length - 1];
}

/** True only when the element itself is the vertical scroller (kiosk lists). */
export function isVerticalScrollContainer(element: HTMLElement): boolean {
  const overflowY = getComputedStyle(element).overflowY;
  if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') {
    return false;
  }
  return element.scrollHeight > element.clientHeight + 1;
}
